'use client';
import {
  Box,
  ButtonBase,
  ButtonProps,
  darken,
  getContrastRatio,
  lighten,
  useTheme,
  CircularProgress,
  Typography,
} from '@mui/material';
import Optional from '@jd/ui/components/Optional';

const buttonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'uppercase',
  px: 3,
  py: 1.5,
  borderRadius: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
};

const AnimatedPulseButton = ({ children, loading, ...rest }: ButtonProps) => {
  const theme = useTheme();
  const { loadingPosition, ...baseButtonProps } = rest;

  const contrastToDark = getContrastRatio(theme.palette.primary.main, '#000');
  const bgFn = contrastToDark > 2 ? darken : lighten;
  const pulseColor = theme.palette.primary.main;

  return (
    <ButtonBase
      {...baseButtonProps}
      disabled={loading || baseButtonProps.disabled}
      sx={{
        ...buttonSx,
        transition: 'background-color 0.2s ease',
        '@keyframes pulse-ring': {
          '0%': { boxShadow: `0 0 0 0 ${pulseColor}80` },
          '70%': { boxShadow: `0 0 0 10px ${pulseColor}00` },
          '100%': { boxShadow: `0 0 0 0 ${pulseColor}00` },
        },
        ':hover, :focus': {
          textDecoration: 'none !important',
          backgroundColor: bgFn(theme.palette.primary.main, 0.1),
          animation: loading
            ? 'none'
            : 'pulse-ring 1.5s infinite cubic-bezier(0.66, 0, 0, 1)',
        },
        ...baseButtonProps.sx,
      }}
    >
      <Typography
        variant={'button'}
        sx={{
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.2s ease',
          fontSize: 15,
          lineHeight: 1.75,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>

      <Optional condition={!!loading}>
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
        </Box>
      </Optional>
    </ButtonBase>
  );
};

export default AnimatedPulseButton;
